import { defineComponent, ref, computed, toRef, watch } from 'vue-demi';
import { D as DropdownClose, a as DropdownOpen } from './dropdown-open-06d651cf.js';
import { u as useDateFormat, b as useTimeFormat, a as useLocale } from './useTimeFormat-7ad4cc11.js';
import './dom-53c9635b.js';
import '@vueuse/core';
import { u as useProxyModelValue } from './useProxyModelValue-e18f251e.js';
import '@onereach/styles/screens.json';
import '@onereach/styles/tailwind.config.json';
import './TimeFormat-a7f5565b.js';
import { s as script$9 } from './OrError-331220bc.js';
import { s as script$a } from './OrHint-262c8217.js';
import { s as script$c } from './OrIconButton-5a508869.js';
import { s as script$b } from './OrIcon-9aff520e.js';
import { s as script$d, a as InputBoxVariant } from './OrInputBox-2f33498c.js';
import { s as script$e } from './OrLabel-4abbce6e.js';
import { s as script$f } from './OrPopover-0daeba14.js';
import { s as script$1, a as script$2, b as script$3, c as script$4, d as script$5, e as script$6, f as script$7, g as script$8 } from './OrDateTimePickerTimeSelect-149ec6c0.js';
import { g as getCurrentDate, f as formatMobileDate, a as formatMobileTime } from './getCurrentDate-e5a478c3.js';
import { resolveComponent, resolveDirective, openBlock, createElementBlock, normalizeClass, createBlock, withCtx, renderSlot, createTextVNode, toDisplayString, createCommentVNode, withDirectives, withModifiers, createVNode, vShow, createElementVNode } from 'vue';

const DateTimePicker = [
// Layout
'layout-column',
// Spacing
'gap-xs'];
const DateTimePickerDatePopover = [
// Box
'w-[298px]'];
const DateTimePickerTimePopover = [];

var script = defineComponent({
  name: 'OrDateTimePicker',
  components: {
    OrDateTimePickerDateControl: script$1,
    OrDateTimePickerDateSelect: script$2,
    OrDateTimePickerMobileControl: script$3,
    OrDateTimePickerMonthSelect: script$4,
    OrDateTimePickerPopoverFooter: script$5,
    OrDateTimePickerPopoverHeader: script$6,
    OrDateTimePickerTimeControl: script$7,
    OrDateTimePickerTimeSelect: script$8,
    OrError: script$9,
    OrHint: script$a,
    OrIcon: script$b,
    OrIconButton: script$c,
    OrInputBox: script$d,
    OrLabel: script$e,
    OrPopover: script$f
  },
  directives: {
    DropdownClose,
    DropdownOpen
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: undefined,
      default: undefined
    },
    dateFormat: {
      type: [String, Function],
      default: undefined
    },
    timeFormat: {
      type: [String, Function],
      default: undefined
    },
    locale: {
      type: String,
      default: undefined
    },
    yearFilter: {
      type: Function,
      default: undefined
    },
    monthFilter: {
      type: Function,
      default: undefined
    },
    dateFilter: {
      type: Function,
      default: undefined
    },
    hoursFilter: {
      type: Function,
      default: undefined
    },
    minutesFilter: {
      type: Function,
      default: undefined
    },
    variant: {
      type: String,
      default: undefined
    },
    size: {
      type: String,
      default: undefined
    },
    anchor: {
      type: Object,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    error: {
      type: [String, Boolean],
      default: undefined
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'input', 'keydown', 'keyup', 'focus', 'blur'],
  expose: ['root', 'focus', 'blur'],
  setup(props, context) {
    var _a;
    // Refs & Styles
    const root = ref();
    const rootStyles = computed(() => ['or-date-time-picker-v3', ...DateTimePicker, ...(props.variant === InputBoxVariant.Embedded ? ['grow'] : [])]);
    const inputBox = ref();
    const inputBoxRoot = computed(() => {
      var _a;
      return (_a = inputBox.value) === null || _a === void 0 ? void 0 : _a.root;
    });
    const datePopover = ref();
    const datePopoverState = computed(() => {
      var _a;
      return (_a = datePopover.value) === null || _a === void 0 ? void 0 : _a.state;
    });
    const datePopoverStyles = computed(() => ['or-date-time-picker-date-popover-v3', ...DateTimePickerDatePopover]);
    const timePopover = ref();
    const timePopoverState = computed(() => {
      var _a;
      return (_a = timePopover.value) === null || _a === void 0 ? void 0 : _a.state;
    });
    const timePopoverStyles = computed(() => ['or-date-time-picker-time-popover-v3', ...DateTimePickerTimePopover]);
    // Options
    const resolvedDateFormat = useDateFormat(toRef(props, 'dateFormat'));
    const resolvedTimeFormat = useTimeFormat(toRef(props, 'timeFormat'));
    const resolvedLocale = useLocale(toRef(props, 'locale'));
    // State
    const initialValue = ref((_a = props.modelValue) !== null && _a !== void 0 ? _a : getCurrentDate());
    watch(() => props.modelValue, value => {
      initialValue.value = value !== null && value !== void 0 ? value : getCurrentDate();
    });
    const model = useProxyModelValue(toRef(props, 'modelValue'), context.emit);
    const draft = ref(model.value);
    watch(model, value => {
      draft.value = value;
    });
    const invalid = computed(() => {
      if (draft.value) {
        if (props.yearFilter && !props.yearFilter(draft.value)) {
          return true;
        }
        if (props.monthFilter && !props.monthFilter(draft.value)) {
          return true;
        }
        if (props.dateFilter && !props.dateFilter(draft.value)) {
          return true;
        }
        if (props.hoursFilter && !props.hoursFilter(draft.value)) {
          return true;
        }
        if (props.minutesFilter && !props.minutesFilter(draft.value)) {
          return true;
        }
        return false;
      }
      return true;
    });
    // Methods
    function apply() {
      model.value = draft.value;
    }
    function reset() {
      draft.value = model.value;
    }
    function toggleDatePopover() {
      if (!props.readonly && !props.disabled) {
        const popoverInstance = datePopover.value;
        if (popoverInstance) {
          if (datePopoverState.value === 'open') {
            popoverInstance.close();
          } else {
            popoverInstance.open();
          }
        }
        closeTimePopover();
      }
    }
    function toggleTimePopover() {
      if (!props.readonly && !props.disabled) {
        const popoverInstance = timePopover.value;
        if (popoverInstance) {
          if (timePopoverState.value === 'open') {
            popoverInstance.close();
          } else {
            popoverInstance.open();
          }
        }
        closeDatePopover();
      }
    }
    function closeDatePopover() {
      const popoverInstance = datePopover.value;
      if (popoverInstance) {
        popoverInstance.close();
      }
    }
    function closeTimePopover() {
      const popoverInstance = timePopover.value;
      if (popoverInstance) {
        popoverInstance.close();
      }
    }
    return {
      root,
      rootStyles,
      inputBox,
      inputBoxRoot,
      datePopover,
      datePopoverState,
      datePopoverStyles,
      timePopover,
      timePopoverState,
      timePopoverStyles,
      resolvedDateFormat,
      resolvedTimeFormat,
      resolvedLocale,
      initialValue,
      model,
      draft,
      invalid,
      apply,
      reset,
      toggleDatePopover,
      toggleTimePopover,
      closeDatePopover,
      closeTimePopover,
      formatMobileDate,
      formatMobileTime
    };
  }
});

const _hoisted_1 = /*#__PURE__*/createElementVNode("div", {
  class: /*#__PURE__*/normalizeClass(['grow'])
}, null, -1 /* HOISTED */);
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrLabel = resolveComponent("OrLabel");
  const _component_OrDateTimePickerDateControl = resolveComponent("OrDateTimePickerDateControl");
  const _component_OrDateTimePickerTimeControl = resolveComponent("OrDateTimePickerTimeControl");
  const _component_OrDateTimePickerMobileControl = resolveComponent("OrDateTimePickerMobileControl");
  const _component_OrDateTimePickerPopoverHeader = resolveComponent("OrDateTimePickerPopoverHeader");
  const _component_OrDateTimePickerMonthSelect = resolveComponent("OrDateTimePickerMonthSelect");
  const _component_OrDateTimePickerDateSelect = resolveComponent("OrDateTimePickerDateSelect");
  const _component_OrDateTimePickerPopoverFooter = resolveComponent("OrDateTimePickerPopoverFooter");
  const _component_OrPopover = resolveComponent("OrPopover");
  const _component_OrDateTimePickerTimeSelect = resolveComponent("OrDateTimePickerTimeSelect");
  const _component_OrIconButton = resolveComponent("OrIconButton");
  const _component_OrIcon = resolveComponent("OrIcon");
  const _component_OrInputBox = resolveComponent("OrInputBox");
  const _component_OrHint = resolveComponent("OrHint");
  const _component_OrError = resolveComponent("OrError");
  const _directive_dropdown_open = resolveDirective("dropdown-open");
  const _directive_dropdown_close = resolveDirective("dropdown-close");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.$slots.label || _ctx.$slots.addon || _ctx.label ? (openBlock(), createBlock(_component_OrLabel, {
    key: 0,
    variant: 'input',
    required: _ctx.required,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "label", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["required", "disabled"])) : createCommentVNode("v-if", true), withDirectives((openBlock(), createBlock(_component_OrInputBox, {
    ref: 'inputBox',
    variant: _ctx.variant,
    size: _ctx.size,
    invalid: !!_ctx.error,
    readonly: _ctx.readonly,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [_ctx.modelValue ? (openBlock(), createBlock(_component_OrIconButton, {
      key: 0,
      icon: 'close',
      color: 'inherit',
      disabled: _ctx.readonly || _ctx.disabled,
      onClick: _cache[11] || (_cache[11] = withModifiers($event => _ctx.$emit('update:modelValue', undefined), ["stop"]))
    }, null, 8 /* PROPS */, ["disabled"])) : (openBlock(), createBlock(_component_OrIcon, {
      key: 1,
      icon: 'calendar_month',
      variant: 'outlined'
    }))]),
    default: withCtx(() => [withDirectives(createVNode(_component_OrDateTimePickerDateControl, {
      modelValue: _ctx.draft,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.draft = $event),
      format: _ctx.resolvedDateFormat,
      locale: _ctx.resolvedLocale,
      selected: _ctx.datePopover && _ctx.datePopover.state === 'open',
      required: _ctx.required,
      readonly: _ctx.readonly,
      disabled: _ctx.disabled
    }, null, 8 /* PROPS */, ["modelValue", "format", "locale", "selected", "required", "readonly", "disabled"]), [[_directive_dropdown_open, _ctx.toggleDatePopover], [_directive_dropdown_close, _ctx.closeDatePopover]]), withDirectives(createVNode(_component_OrDateTimePickerTimeControl, {
      modelValue: _ctx.draft,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => _ctx.draft = $event),
      format: _ctx.resolvedTimeFormat,
      locale: _ctx.resolvedLocale,
      selected: _ctx.timePopover && _ctx.timePopover.state === 'open',
      required: _ctx.required,
      readonly: _ctx.readonly,
      disabled: _ctx.disabled
    }, null, 8 /* PROPS */, ["modelValue", "format", "locale", "selected", "required", "readonly", "disabled"]), [[_directive_dropdown_open, _ctx.toggleTimePopover], [_directive_dropdown_close, _ctx.closeTimePopover]]), createVNode(_component_OrPopover, {
      ref: 'datePopover',
      class: normalizeClass(_ctx.datePopoverStyles),
      "custom-styles": _ctx.datePopoverStyles,
      variant: 'auto',
      "bottom-sheet-props": {
        variant: 'basic'
      },
      trigger: _ctx.anchor || _ctx.inputBoxRoot,
      placement: 'bottom-start',
      "fallback-placement": 'start',
      onClose: _cache[6] || (_cache[6] = $event => _ctx.reset())
    }, {
      default: withCtx(() => [createVNode(_component_OrDateTimePickerPopoverHeader, null, {
        default: withCtx(() => [createVNode(_component_OrDateTimePickerMobileControl, {
          label: 'Date'
        }, {
          default: withCtx(() => [createTextVNode(toDisplayString(_ctx.draft ? _ctx.formatMobileDate(_ctx.draft, _ctx.resolvedDateFormat, _ctx.resolvedLocale) : '--'), 1 /* TEXT */)]),
          _: 1 /* STABLE */
        }), createVNode(_component_OrDateTimePickerMobileControl, {
          label: 'Time'
        }, {
          default: withCtx(() => [createTextVNode(toDisplayString(_ctx.draft ? _ctx.formatMobileTime(_ctx.draft, _ctx.resolvedTimeFormat, _ctx.resolvedLocale) : '--'), 1 /* TEXT */)]),
          _: 1 /* STABLE */
        })]),
        _: 1 /* STABLE */
      }), createVNode(_component_OrDateTimePickerMonthSelect, {
        modelValue: _ctx.initialValue,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => _ctx.initialValue = $event),
        locale: _ctx.resolvedLocale,
        "year-filter": _ctx.yearFilter,
        "month-filter": _ctx.monthFilter
      }, null, 8 /* PROPS */, ["modelValue", "locale", "year-filter", "month-filter"]), createVNode(_component_OrDateTimePickerDateSelect, {
        modelValue: _ctx.draft,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => _ctx.draft = $event),
        "initial-value": _ctx.initialValue,
        format: _ctx.resolvedDateFormat,
        locale: _ctx.resolvedLocale,
        "date-filter": _ctx.dateFilter
      }, null, 8 /* PROPS */, ["modelValue", "initial-value", "format", "locale", "date-filter"]), createVNode(_component_OrDateTimePickerPopoverFooter, {
        disabled: _ctx.invalid,
        onApply: _cache[4] || (_cache[4] = $event => {
          _ctx.apply();
          _ctx.datePopover && _ctx.datePopover.close();
        }),
        onReset: _cache[5] || (_cache[5] = $event => {
          _ctx.reset();
          _ctx.datePopover && _ctx.datePopover.close();
        })
      }, null, 8 /* PROPS */, ["disabled"])]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["class", "custom-styles", "trigger"]), createVNode(_component_OrPopover, {
      ref: 'timePopover',
      class: normalizeClass(_ctx.timePopoverStyles),
      "custom-styles": _ctx.timePopoverStyles,
      variant: 'auto',
      "bottom-sheet-props": {
        variant: 'basic'
      },
      trigger: _ctx.anchor || _ctx.inputBoxRoot,
      placement: 'bottom-start',
      "fallback-placement": 'start',
      onClose: _cache[10] || (_cache[10] = $event => _ctx.reset())
    }, {
      default: withCtx(() => [createVNode(_component_OrDateTimePickerPopoverHeader, null, {
        default: withCtx(() => [createVNode(_component_OrDateTimePickerMobileControl, {
          label: 'Date'
        }, {
          default: withCtx(() => [createTextVNode(toDisplayString(_ctx.draft ? _ctx.formatMobileDate(_ctx.draft, _ctx.resolvedDateFormat, _ctx.resolvedLocale) : '--'), 1 /* TEXT */)]),
          _: 1 /* STABLE */
        }), createVNode(_component_OrDateTimePickerMobileControl, {
          label: 'Time'
        }, {
          default: withCtx(() => [createTextVNode(toDisplayString(_ctx.draft ? _ctx.formatMobileTime(_ctx.draft, _ctx.resolvedTimeFormat, _ctx.resolvedLocale) : '--'), 1 /* TEXT */)]),
          _: 1 /* STABLE */
        })]),
        _: 1 /* STABLE */
      }), createVNode(_component_OrDateTimePickerTimeSelect, {
        modelValue: _ctx.draft,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = $event => _ctx.draft = $event),
        "initial-value": _ctx.initialValue,
        format: _ctx.resolvedTimeFormat,
        locale: _ctx.resolvedLocale,
        "hours-filter": _ctx.hoursFilter,
        "minutes-filter": _ctx.minutesFilter
      }, null, 8 /* PROPS */, ["modelValue", "initial-value", "format", "locale", "hours-filter", "minutes-filter"]), createVNode(_component_OrDateTimePickerPopoverFooter, {
        disabled: _ctx.invalid,
        onApply: _cache[8] || (_cache[8] = $event => {
          _ctx.apply();
          _ctx.timePopover && _ctx.timePopover.close();
        }),
        onReset: _cache[9] || (_cache[9] = $event => {
          _ctx.reset();
          _ctx.timePopover && _ctx.timePopover.close();
        })
      }, null, 8 /* PROPS */, ["disabled"])]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["class", "custom-styles", "trigger"]), _hoisted_1]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["variant", "size", "invalid", "readonly", "disabled"])), [[_directive_dropdown_open, _ctx.toggleDatePopover], [_directive_dropdown_close, _ctx.closeDatePopover]]), _ctx.$slots.hint || _ctx.hint ? withDirectives((openBlock(), createBlock(_component_OrHint, {
    key: 1,
    disabled: _ctx.disabled
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "hint", {}, () => [createTextVNode(toDisplayString(_ctx.hint), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["disabled"])), [[vShow, !_ctx.error]]) : createCommentVNode("v-if", true), withDirectives(createVNode(_component_OrError, {
    disabled: _ctx.disabled
  }, {
    default: withCtx(() => [createTextVNode(toDisplayString(_ctx.error), 1 /* TEXT */)]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["disabled"]), [[vShow, typeof _ctx.error === 'string' && _ctx.error.length > 0]])], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-date-time-picker-v3/OrDateTimePicker.vue";

export { script as s };
